import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import Home from "./pages/Home";
import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";
import {
  SESSION_ENDED_MESSAGE_KEY,
  startSessionKeeper,
} from "./utils/sessionKeeper";
import { UserProvider } from "./components/UserContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/layouts/InfluencerLayout";
import InternalGuard from "./components/InternalGuard";
import { HelmetProvider } from "react-helmet-async";
import GlobalNetworkStatus from "./components/GlobalNetworkStatus";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import ModuleSelection from "./pages/ModuleSelection";

const AddInfluencer = lazy(() => import("./pages/AddInfluencer"));
const AdminDashboard = lazy(() => import("./Dashboard/AdminDashboard"));
const ApplicationPage = lazy(() => import("./pages/AppicationPage"));
// DISABLED (URL restructure pending): const ApplicationPage3 = lazy(() => import("./pages/brandGst"));
const BrandAnalytics = lazy(() => import("./pages/BrandAnalytics"));
const BrandDashboard = lazy(() => import("./Dashboard/Brand_Dashboard"));
const BrandTermsOfUse = lazy(() => import("./pages/BrandTermsOfUse"));
// DISABLED (URL restructure pending): const CampaignOverview = lazy(() => import("./pages/CampaignOverview"));
const CountryPage = lazy(() => import("./pages/CountryPage"));
const Signup = lazy(() => import("./pages/Signup"));
const ExecutiveDashboard = lazy(() => import("./Dashboard/ExecutiveDashboard"));
// DISABLED (URL restructure pending): const FacebookLoginError = lazy(() => import("./pages/FacebookLoginError"));
const ForgetInternal = lazy(() => import("./pages/ForgetInternal"));
const ForgetPassword = lazy(() => import("./pages/ForgetPassword"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const GoogleMapsProvider = lazy(() =>
  import("./components/GoogleMapsProvider")
);
const InfDelverablesForm = lazy(() => import("./pages/influencrDeliverables"));
const InfluencerApplicationTable = lazy(() =>
  import("./pages/InfluencerApplicationTable")
);
const InfluencerLogin = lazy(() => import("./pages/InfluencerLogin"));
const InfluencerSignup = lazy(() => import("./pages/InfluencerSignup"));
const InfluencerTermsOfUse = lazy(() => import("./pages/InfluencerTermsOfUse"));
const InfluencerconfirmationPage = lazy(() =>
  import("./pages/confirmationPage")
);
// DISABLED (URL restructure pending): const InstagramError = lazy(() => import("./pages/InstagramError"));
const InstagramInsights = lazy(() => import("./pages/InstagramInsights"));
const InternalSignin = lazy(() => import("./pages/InternalSignin"));
const InternalSignup = lazy(() => import("./pages/InternalSignup"));
const LeadDashboard = lazy(() => import("./Dashboard/LeadDashboard"));
const CreatorDatabaseDashboard = lazy(() =>
  import("./Dashboard/CreatorDatabaseDashboard")
);
const Live = lazy(() => import("./pages/live"));
const LiveLinks = lazy(() => import("./pages/LiveLinks"));
// DISABLED (URL restructure pending): LocationAutocomplete

const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const OTP = lazy(() => import("./pages/mobilevalidation"));
const OtpInternal = lazy(() => import("./pages/OtpInternal"));
const OtpInternal1 = lazy(() => import("./pages/OtpInternal1"));
const OtpPage = lazy(() => import("./pages/OtpPage"));
const RequestCallback = lazy(() => import("./pages/RequestCallback"));
const RequestDemo = lazy(() => import("./pages/RequestDemo"));
const SignInBrand = lazy(() => import("./pages/SignInBrand"));
// const SignUp = lazy(() => import("./pages/SignUp"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const UpdatePasswordInternal = lazy(() =>
  import("./pages/UpdatePasswordInternal")
);
const UserListInsta = lazy(() => import("./pages/UsersListInsta"));
const UserPendingApproval = lazy(() => import("./pages/UserPendingApproval"));
// DISABLED (URL restructure pending): const UserSignIn = lazy(() => import("./pages/userSign"));
// DISABLED (URL restructure pending): const UserSignup = lazy(() => import("./pages/userSignup"));
const ViewAnalytics = lazy(() => import("./pages/ViewAnalytics"));
const ViewCampaignBrand = lazy(() => import("./pages/ViewCampaignBrand"));

// Single internal entry point (/internal/home) — the four legacy dashboard URLs
// (admin / lead / campaign-manager / sales) collapsed into a role dispatcher.
// ?view= query params (e.g. new-deals, user-roles) pass straight through.
const InternalHome = () => {
  const role = String(localStorage.getItem("userRole") || "").toLowerCase();
  const [searchParams] = useSearchParams();
  // Entry Role only has the Creator Database. Every other role keeps its own
  // dashboard and reaches the Creator Database from the sidebar (?view=).
  if (role === "entry" || searchParams.get("view") === "creator-database") {
    return <CreatorDatabaseDashboard />;
  }
  if (role === "lead") return <LeadDashboard />;
  if (role === "executive") return <ExecutiveDashboard />;
  return <AdminDashboard />; // admin + sales + fallback
};

// Roles that can open /internal/home. The Creator Database tab
// (?view=creator-database) is additionally open to every internal role,
// including community-only ones, since an admin can grant any role access -
// the page itself shows "no access" when the role hasn't been granted it.
const INTERNAL_HOME_ROLES = ["admin", "lead", "executive", "sales", "entry"];
const CREATOR_DATABASE_ROLES = [
  ...INTERNAL_HOME_ROLES,
  "Campaign_Manger",
  "Payment_Approver",
  "Support_Closer",
];

const InternalHomeGuard = () => {
  const [searchParams] = useSearchParams();
  return (
    <ProtectedRoute
      allowedRoles={
        searchParams.get("view") === "creator-database"
          ? CREATOR_DATABASE_ROLES
          : INTERNAL_HOME_ROLES
      }
    />
  );
};

const ROUTE_SUSPENSE_DELAY_MS = 7000;
const INFLUENCER_BRAND_REGISTRATION_URL = `${window.location.origin}/creators/brand/register`;

const loadAppStyles = () => import("./App.css");

const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
};

const RouteSuspenseFallback = () => {
  const [showFallback, setShowFallback] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );

  useEffect(() => {
    const showIfOffline = () => setShowFallback(true);
    const delayTimer = window.setTimeout(() => {
      setShowFallback(true);
    }, ROUTE_SUSPENSE_DELAY_MS);

    window.addEventListener("offline", showIfOffline);

    return () => {
      window.clearTimeout(delayTimer);
      window.removeEventListener("offline", showIfOffline);
    };
  }, []);

  if (!showFallback) {
    return null;
  }

  return (
    <div
      className="global-network-overlay"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="global-network-overlay__content">
        <div className="global-network-overlay__spinner" aria-hidden="true" />
        <p className="global-network-overlay__title">Still loading</p>
        <p className="global-network-overlay__message">
          Loading is taking longer than usual because of the current network
          connection.
        </p>
      </div>
    </div>
  );
};

// The community app rewrites the shared localStorage "userRole" from
// "admin"/"lead" to "Admin"/"Lead" for its own case-sensitive checks (see
// community/frontend/src/App.js ensureCommunityRoleCasing). This app compares
// against the lowercase unified roles, so undo that on every load - otherwise
// e.g. a Lead coming from the community app is treated as a brand user and
// bounced to the home page.
// Its brand dashboard likewise stores a brand as "Brand" (community/frontend
// src/pages/brand.js), which the brand dashboards' campaign list (filtered by
// role === "brand") doesn't recognise.
const COMMUNITY_TO_UNIFIED_ROLE = {
  Admin: "admin",
  Lead: "lead",
  Brand: "brand",
};
const ensureUnifiedRoleCasing = () => {
  const storedRole = localStorage.getItem("userRole");
  let unifiedRole = storedRole && COMMUNITY_TO_UNIFIED_ROLE[storedRole];
  // The community app also stores a creator-side Campaign Manager
  // ("executive") as "Campaign_Manger". Only creator-side Campaign Managers
  // have the Creator Marketing module - community-only ones keep their role.
  if (storedRole === "Campaign_Manger") {
    let modules = [];
    try {
      modules = JSON.parse(localStorage.getItem("adminModules") || "[]");
    } catch (error) {
      modules = [];
    }
    if (Array.isArray(modules) && modules.includes("creatorMarketing")) {
      unifiedRole = "executive";
    }
  }
  if (unifiedRole) localStorage.setItem("userRole", unifiedRole);
};

const App = () => {
  ensureUnifiedRoleCasing();
  useEffect(() => {
    window.dispatchEvent(new Event("hobo.video:app-ready"));
  }, []);

  useEffect(() => {
    let idleId;
    let timeoutId;
    let scheduled = false;
    const isHomeRoute = window.location.pathname === "/";

    const loadStyles = () => {
      loadAppStyles();
    };

    if (!isHomeRoute) {
      loadStyles();
      return undefined;
    }

    const scheduleStyles = () => {
      if (scheduled) return;
      scheduled = true;

      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(loadStyles, { timeout: 3000 });
      } else {
        timeoutId = window.setTimeout(loadStyles, 1800);
      }
    };

    if (document.readyState === "complete") {
      timeoutId = window.setTimeout(scheduleStyles, 1200);
    } else {
      window.addEventListener("load", scheduleStyles, { once: true });
      timeoutId = window.setTimeout(scheduleStyles, 3000);
    }

    return () => {
      window.removeEventListener("load", scheduleStyles);
      if (idleId) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => startSessionKeeper(), []);

  // A session that ended on its own (expired, or ended from another app)
  // lands here on a sign-in page - say why instead of a silent logout.
  useEffect(() => {
    let message = null;
    try {
      message = sessionStorage.getItem(SESSION_ENDED_MESSAGE_KEY);
      sessionStorage.removeItem(SESSION_ENDED_MESSAGE_KEY);
    } catch (err) {
      // Storage blocked - nothing to show.
    }
    if (message) toast.info(message);
  }, []);

  useEffect(() => {
    let timeout = null;

    const updateActivity = () => {
      const token = localStorage.getItem("token");
      const role = localStorage.getItem("userRole");

      // 🚫 Ignore if not authenticated properly
      if (!token || !role) return;

      // ⏱ Throttle (prevent excessive writes)
      if (timeout) return;

      timeout = setTimeout(() => {
        localStorage.setItem("lastActivity", Date.now().toString());
        timeout = null;
      }, 1000);
    };

    // const events = ["click", "scroll", "keydown"];
    const events = ["click", "keydown"];

    events.forEach((event) => window.addEventListener(event, updateActivity));

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, updateActivity)
      );

      if (timeout) clearTimeout(timeout); // cleanup safety
    };
  }, []);

  // const [isAuthenticated, setIsAuthenticated] = useState(
  //   !!localStorage.getItem("token") // Example: Check if token exists
  // );
  // console.log("Is Authenticated:", isAuthenticated);

  return (
    <div className="hero">
      <HelmetProvider>
        {/* <GoogleMapsProvider> */}
        {/* <BrowserRouter> */}
        <BrowserRouter basename="/">
          {/* <BrowserRouter basename="/influencer"> */}
          {/* <AuthRedirectGate> */}
          <UserProvider>
            <GlobalNetworkStatus />
            <ErrorBoundary>
              <Suspense fallback={<RouteSuspenseFallback />}>
                <Routes>
                  {/* Test Routes */}
                  {/* DISABLED (URL restructure pending):
                <Route
                  path="/location"
                  element={
                    <GoogleMapsProvider>
                      <LocationAutocomplete />
                    </GoogleMapsProvider>
                  }
                />
                */}

                  {/* Public Routes */}
                  {/* <Route path="/" element={<Home />} /> */}
                  <Route path="/get-started" element={<GetStarted />} />
                  {/* <Route path="/home-v1-1" element={<HomeV1_1 />} /> */}
                  {/* <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} /> */}
                  {/* SEO URL restructure: role is carried in the path instead of ?role= */}
                  <Route
                    path="/member/signup"
                    element={<Signup roleOverride="member" />}
                  />
                  <Route
                    path="/brand/signup"
                    element={<Signup roleOverride="brand" />}
                  />
                  <Route
                    path="/member/signin"
                    element={<Login roleOverride="member" />}
                  />
                  <Route
                    path="/brand/signin"
                    element={<Login roleOverride="brand" />}
                  />
                  {/* <Route path="/brand-signup" element={<SignUp />} /> */}
                  {/* DISABLED (URL restructure pending):
                <Route
                  path="/complete-brand-profile"
                  element={
                    <ExternalRedirect
                      to={`${INFLUENCER_BRAND_REGISTRATION_URL}${window.location.search}`}
                    />
                  }
                />
                <Route
                  path="/brand-representative-information"
                  element={<ApplicationPage3 />}
                />
                */}
                  {/* <Route path="/otp/:Email" element={<OTP />} /> */}
                  {/* Brand & Influencer Sign Up */}
                  {/* URL restructure: signup OTP verification is now /otp-verify
                    (was /verify-otp - kept as a redirect for in-flight links). */}
                  <Route path="/otp-verify" element={<OTP />} />
                  <Route
                    path="/verify-otp"
                    element={<Navigate to="/otp-verify" replace />}
                  />
                  <Route path="/forgot-password" element={<ForgetPassword />} />
                  <Route
                    path="/reset-password/:token"
                    element={<UpdatePassword />}
                  />
                  {/* <Route path="/brand-login" element={<SignInBrand />} /> */}
                  <Route
                    path="/internal/forgot-password"
                    element={<ForgetInternal />}
                  />
                  {/* <Route path="/otpinternal/:email" element={<OtpInternal />} /> */}
                  {/* Internal Sign Up */}
                  <Route
                    path="/internal/otp-verify"
                    element={<OtpInternal />}
                  />
                  <Route
                    path="/internal/verify-otp"
                    element={<Navigate to="/internal/otp-verify" replace />}
                  />
                  {/* Internal Forget Password OTP - moved off /internal/otp-verify
                    (now the internal signup OTP route) to a password-reset-scoped
                    path. */}
                  {/* <Route path="/otpinternal1/:email" element={<OtpInternal1 />} /> */}
                  <Route
                    path="/internal/forgot-password/otp-verify"
                    element={<OtpInternal1 />}
                  />
                  <Route path="/internal/signup" element={<InternalSignup />} />
                  <Route
                    path="/internal/signin"
                    element={
                      <InternalGuard>
                        <InternalSignin />
                      </InternalGuard>
                    }
                  />
                  <Route
                    path="/internal/pending-approval"
                    element={<UserPendingApproval />}
                  />
                  {/* <Route
                    path="/influencer-login"
                    element={<InfluencerLogin />}
                  />
                  <Route
                    path="/influencer-signup"
                    element={<InfluencerSignup />}
                  /> */}
                  {/* Brand & Influencer Forget Password OTP - moved off
                    /otp-verify (now the signup OTP route) to a
                    password-reset-scoped path. */}
                  {/* <Route path="/otp1/:email" element={<OtpPage />} /> */}
                  <Route
                    path="/forgot-password/otp-verify"
                    element={<OtpPage />}
                  />
                  <Route
                    path="/internal/reset-password/:token"
                    element={<UpdatePasswordInternal />}
                  />
                  {/* DISABLED (URL restructure pending):
                <Route path="/userSignUp/:brandName" element={<UserSignup />} />
                <Route path="/userSignIn" element={<UserSignIn />} />
                <Route
                  path="/instagram-insights"
                  element={<InstagramInsights />}
                />
                */}
                  {/* <Route
                    path="/brand-analytics/:campaignName"
                    element={<BrandAnalytics />}
                  /> */}
                  <Route
                    path="/terms/user"
                    element={<InfluencerTermsOfUse />}
                  />
                  <Route path="/terms/brand" element={<BrandTermsOfUse />} />
                  {/* DISABLED (URL restructure pending):
                <Route
                  path="/campaign-overview/:brandName/:campaignName"
                  element={<CampaignOverview />}
                />
                <Route
                  path="/facebook-login-error"
                  element={<FacebookLoginError />}
                />
                <Route
                  path="/auth/instagram/error"
                  element={<InstagramError />}
                />
                */}
                  <Route path="/brand/request-demo" element={<RequestDemo />} />
                  <Route
                    path="/brand/request-callback"
                    element={<RequestCallback />}
                  />
                  {/* DISABLED (URL restructure pending):
                <Route
                  path="/brand-collaboration-live-campaigns"
                  element={
                    <Live />
                  }
                />
                */}

                  {/* All Authenticated Users Can Access These */}
                  {/* <Route element={<ProtectedRoute />}> */}
                  {/* Influencer Routes */}

                  {/* <Route
                      path="/brand-collaboration-live-campaigns"
                      element={<Live />}
                    /> */}
                  {/* <Route
                      path="/thank-you"
                      element={
                        <Layout>
                          <ThankYou />
                        </Layout>
                      }
                    /> */}
                  {/* <Route
                      path="/application-form"
                      element={
                        <Layout>
                          <ApplicationPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/confirmation-page"
                      element={
                        <Layout>
                          <InfluencerconfirmationPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/inf-deliverbles"
                      element={
                        <Layout>
                          <InfDelverablesForm />
                        </Layout>
                      }
                    />
                    <Route
                      path="/live-links"
                      element={
                        <Layout>
                          <LiveLinks />
                        </Layout>
                      }
                    /> */}
                  {/* <Route path="/success-page" element={<SuccessPage />} /> */}
                  {/* <Route
                      path="/view-campaignBrand/:brandName/:campaignName"
                      element={<ViewCampaignBrand />}
                    />
                    <Route path="/influencers" element={<UserListInsta />} />
                    <Route
                      path="/instagram-insights/:instagramAccountId"
                      // element={<UserDetailPageInsta />}
                      element={<InstagramInsights />}
                    /> */}
                  {/* <Route path="/brand-application" element={<BrandPage />} /> */}
                  {/* <Route
                      path="/view-analytics/:campaignName"
                      element={<ViewAnalytics />}
                    />
                    <Route
                      path="/add-influencer/:brandName/:campaignName"
                      element={<AddInfluencer />}
                    /> */}
                  {/* </Route> */}

                  {/* Internal Routes */}
                  <Route
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "admin",
                          "lead",
                          "executive",
                          "brand",
                          "brand-admin",
                          "brand-staff",
                        ]}
                      />
                    }
                  ></Route>

                  {/* Internal Routes (admin / lead / executive / sales) --
                    the four legacy dashboard URLs collapsed into /internal/home
                    (role-dispatched by <InternalHome />). */}
                  <Route element={<InternalHomeGuard />}>
                    <Route
                      path="/internal/home"
                      element={
                        <GoogleMapsProvider>
                          <InternalHome />
                        </GoogleMapsProvider>
                      }
                    />
                    {/* <Route
                      path="/tbladmin/:brandName/:campaignName"
                      element={<InfluencerApplicationTable />}
                    />
                    <Route
                      path="/tbllead/:brandName/:campaignName"
                      element={<InfluencerApplicationTable />}
                    />
                    <Route
                      path="/influencer-table-campaign-manager/:brandName/:campaignName"
                      element={<InfluencerApplicationTable />}
                    /> */}
                  </Route>

                  {/* Brand Routes */}
                  <Route
                    element={
                      <ProtectedRoute
                        allowedRoles={["brand", "brand-admin", "brand-staff"]}
                      />
                    }
                  >
                    {/* <Route
                      path="/brand-dashboard"
                      element={
                        <GoogleMapsProvider>
                          <BrandDashboard />
                        </GoogleMapsProvider>
                      }
                    /> */}
                    <Route
                      path="/module-selection"
                      element={<ModuleSelection />}
                    />
                    {/* <Route
                      path="/brand-registration"
                      element={
                        <ExternalRedirect
                          to={`${INFLUENCER_BRAND_REGISTRATION_URL}${window.location.search}`}
                        />
                      }
                    /> */}
                    {/* <Route
                      path="/tbl1/:brandName/:campaignName"
                      // element={<InfluencerTableBrand1 />}
                      element={<InfluencerApplicationTable />}
                    /> */}
                  </Route>

                  {/* Country landing pages — /country/<slug> (e.g. /country/canada).
                    Nested under one /country prefix so the Apache proxy whitelist
                    needs the word `country` only once; unknown slugs render
                    NotFoundPage from inside CountryPage. */}
                  <Route path="/country/:countrySlug" element={<CountryPage />} />

                  {/* Bare mount points that aren't real pages. `/auth` is only
                    the CRA asset-path prefix -> hand off to the site root (the
                    static marketing home). `/influencer` is the old name of the
                    creator app, now served at `/creators`. ExternalRedirect does
                    a real document navigation so the server can route these. */}
                  <Route path="/auth" element={<ExternalRedirect to="/" />} />
                  <Route
                    path="/influencer"
                    element={<ExternalRedirect to="/creators/" />}
                  />
                  <Route
                    path="/influencer/*"
                    element={<ExternalRedirect to="/creators/" />}
                  />

                  {/* Catch-All */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <ToastContainer />
          </UserProvider>
          {/* </AuthRedirectGate> */}
        </BrowserRouter>
        {/* </GoogleMapsProvider> */}
      </HelmetProvider>
    </div>
  );
};

export default App;
